import Phaser from 'phaser';
import { CONFIG } from '../config.js';
import { Player } from '../entities/Player.js';
import { Bullet } from '../entities/Bullet.js';
import { SpawnSystem } from '../systems/SpawnSystem.js';
import { WaveSystem } from '../systems/WaveSystem.js';
import { AnswerButtons } from '../ui/AnswerButtons.js';
import { VirtualJoystick } from '../ui/VirtualJoystick.js';
import {
  regenerateOptions,
  VERBS_BASIC,
  ADVANCED_WAVES,
  EXPERT_WAVES,
  getVerbsByFamily,
} from '../data/verbs.js';
import { getAudio } from '../systems/AudioSystem.js';
import { SaveSystem } from '../systems/SaveSystem.js';

const C = CONFIG.COLORS;

const ZONE_NAMES = {
  basic:    'WHISPERING\nFOREST',
  advanced: 'CRYSTAL\nCAVES',
  expert:   "WIZARDS\nSANCTUM",
};

export class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
  }

  init(data = {}) {
    this.level = data.level || 'basic';
    this.askMode = this.level === 'advanced' ? 'past' : 'random';
    this.levelWaves = CONFIG.LEVEL_WAVES[this.level] || CONFIG.LEVEL_WAVES.basic;

    // Verb-pool plans by wave index. For basic, all waves share VERBS_BASIC.
    if (this.level === 'basic') {
      this.waveVerbPools = this.levelWaves.map(() => VERBS_BASIC);
      this.waveFamilyPlans = this.levelWaves.map(() => null);
    } else {
      const planSource = this.level === 'advanced' ? ADVANCED_WAVES : EXPERT_WAVES;
      this.waveFamilyPlans = planSource;
      this.waveVerbPools = planSource.map((p) => getVerbsByFamily(p.families));
    }
    // Default until a wave starts
    this.currentVerbPool = this.waveVerbPools[0] || VERBS_BASIC;

    this.runStats = {
      score: 0,
      kills: 0,
      correct: 0,
      wrong: 0,
      bestCombo: 0,
      xp: 0,
      waveReached: 1,
      bossDefeated: false,
      level: this.level,
    };
    this.combo = 0;
    this.activeEnemy = null;
    this.boss = null;
    this.runEnded = false;
  }

  // SpawnSystem / generateQuestion call this to get the active wave's pool.
  getVerbPool() {
    return this.currentVerbPool;
  }

  create() {
    const W = CONFIG.GAME_WIDTH;
    const H = CONFIG.GAME_HEIGHT;

    // Restore audio pref
    const save = SaveSystem.load();
    this.audio = getAudio();
    this.audio.setEnabled(save.audioEnabled !== false);
    this.audio.unlock();

    // Background — tile across the play area only.
    this.background = this.add
      .tileSprite(0, 0, W, CONFIG.PLAY_AREA_BOTTOM, 'tile-grass')
      .setOrigin(0, 0)
      .setAlpha(0.85)
      .setDepth(-10);

    // Top header strip & bottom panel border
    this.add
      .rectangle(0, 0, W, CONFIG.PLAY_AREA_TOP, C.UI_PANEL, 0.92)
      .setOrigin(0, 0)
      .setDepth(30);
    this.add
      .rectangle(0, CONFIG.PLAY_AREA_TOP - 1, W, 2, C.UI_PRIMARY, 0.6)
      .setOrigin(0, 0)
      .setDepth(31);
    this.add
      .rectangle(0, CONFIG.PLAY_AREA_BOTTOM, W, 2, C.UI_PRIMARY, 0.6)
      .setOrigin(0, 0)
      .setDepth(31);

    // Constrain player to play area via world bounds.
    this.physics.world.setBounds(0, CONFIG.PLAY_AREA_TOP, W, CONFIG.PLAY_AREA_BOTTOM - CONFIG.PLAY_AREA_TOP);

    // Groups
    this.enemies = this.physics.add.group({ runChildUpdate: true });
    this.pickups = this.physics.add.group({ runChildUpdate: true });
    this.playerBullets = this.physics.add.group({ runChildUpdate: true, classType: Bullet });

    // Player
    const playerY = (CONFIG.PLAY_AREA_TOP + CONFIG.PLAY_AREA_BOTTOM) / 2;
    this.player = new Player(this, W / 2, playerY);

    // Systems
    this.spawnSystem = new SpawnSystem(this);
    this.waveSystem = new WaveSystem(this);
    this.waveSystem.setWaves(this.levelWaves);

    // UI
    this.answerButtons = new AnswerButtons(this);
    this.joystick = new VirtualJoystick(this);

    // Input
    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys({
      W: Phaser.Input.Keyboard.KeyCodes.W,
      A: Phaser.Input.Keyboard.KeyCodes.A,
      S: Phaser.Input.Keyboard.KeyCodes.S,
      D: Phaser.Input.Keyboard.KeyCodes.D,
    });

    // Collisions
    this.physics.add.overlap(this.player, this.enemies, this.onPlayerEnemyContact, null, this);
    this.physics.add.overlap(this.player, this.pickups, this.onPlayerPickup, null, this);
    this.physics.add.overlap(this.playerBullets, this.enemies, this.onBulletEnemy, null, this);

    // Event wiring
    this.events.on('answer', this.onAnswer, this);

    // Wave banner container
    this.banner = this.add
      .text(W / 2, (CONFIG.PLAY_AREA_TOP + CONFIG.PLAY_AREA_BOTTOM) / 2, '', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '32px',
        color: '#fde047',
        stroke: '#0f0d2c',
        strokeThickness: 8,
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(100)
      .setAlpha(0);

    // Family banner — bigger overlay shown at the start of advanced/expert waves.
    this.familyBanner = this.add
      .container(W / 2, (CONFIG.PLAY_AREA_TOP + CONFIG.PLAY_AREA_BOTTOM) / 2)
      .setDepth(101)
      .setAlpha(0);

    this.events.on('wave-start', ({ waveId, idx }) => {
      this.runStats.waveReached = waveId;
      this.currentVerbPool = this.waveVerbPools[idx] || this.currentVerbPool;
      const familyPlan = this.waveFamilyPlans[idx];
      if (familyPlan) {
        this.showFamilyBanner(idx, familyPlan);
      } else {
        const zoneText = ZONE_NAMES[this.level] || `WAVE ${waveId}`;
        this.showBanner(zoneText, '#fde047', 0.85);
      }
      this.audio.levelUp();
    });
    this.events.on('wave-boss-start', () => {
      const bossCfg = (CONFIG.BOSSES && CONFIG.BOSSES[this.level]) || CONFIG.BOSSES.basic;
      this.showBanner(bossCfg.name, '#ef4444', 1.1);
      this.audio.bossRoar();
      this.cameras.main.shake(CONFIG.SHAKE_BOSS_DURATION, CONFIG.SHAKE_BOSS_INTENSITY);
    });
    this.events.on('shield-broken', () => {
      this.audio.hit();
      this.spawnFloatText(this.player.x, this.player.y - 18, 'SHIELD!', 0x60a5fa);
    });

    // Launch HUD overlay scene
    this.scene.launch('HUDScene', { gameScene: this });

    // Cleanup on shutdown
    this.events.once('shutdown', this.shutdownScene, this);
    this.events.once('destroy', this.shutdownScene, this);

    // Start wave 1 after a brief banner
    this.time.delayedCall(500, () => this.waveSystem.startWave(0));
  }

  showBanner(text, colorHex = '#fde047', sizeMult = 1) {
    this.banner.setText(text);
    this.banner.setColor(colorHex);
    this.banner.setFontSize(Math.round(32 * sizeMult));
    this.banner.setAlpha(0).setScale(0.3);
    this.tweens.add({
      targets: this.banner,
      alpha: { from: 0, to: 1 },
      scale: { from: 0.3, to: 1 },
      duration: 280,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: this.banner,
          alpha: 0,
          delay: 900,
          duration: 380,
        });
      },
    });
  }

  showFamilyBanner(idx, plan) {
    // Clear previous content
    this.familyBanner.removeAll(true);

    const totalWaves = this.levelWaves.length;
    const W = CONFIG.GAME_WIDTH;

    const card = this.add
      .rectangle(0, 0, W - 40, 220, 0x0f0d2c, 0.92)
      .setStrokeStyle(3, 0xd946ef, 0.95);
    this.familyBanner.add(card);

    const waveLabel = this.add
      .text(0, -86, `WAVE ${plan.wave} / ${totalWaves}`, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '14px',
        color: '#fde047',
        stroke: '#0f0d2c',
        strokeThickness: 4,
      })
      .setOrigin(0.5);
    this.familyBanner.add(waveLabel);

    const familyTitle = this.add
      .text(0, -50, plan.label || 'FAMILY', {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '16px',
        color: '#06b6d4',
        stroke: '#0f0d2c',
        strokeThickness: 4,
        align: 'center',
        wordWrap: { width: W - 60, useAdvancedWrap: true },
      })
      .setOrigin(0.5);
    this.familyBanner.add(familyTitle);

    if (plan.description) {
      const desc = this.add
        .text(0, -22, plan.description, {
          fontFamily: '"Press Start 2P", monospace',
          fontSize: '8px',
          color: '#fbbf24',
          align: 'center',
          wordWrap: { width: W - 60, useAdvancedWrap: true },
        })
        .setOrigin(0.5);
      this.familyBanner.add(desc);
    }

    // Verb preview (first 6 from the pool)
    const pool = this.waveVerbPools[idx] || [];
    const previewWords = pool.slice(0, 6).map((v) => v.base).join('  ·  ');
    const more = pool.length > 6 ? `  +${pool.length - 6}` : '';
    const preview = this.add
      .text(0, 14, previewWords + more, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '9px',
        color: '#ffffff',
        align: 'center',
        wordWrap: { width: W - 60, useAdvancedWrap: true },
      })
      .setOrigin(0.5);
    this.familyBanner.add(preview);

    const zoneLine = this.add
      .text(0, 70, ZONE_NAMES[this.level].replace('\n', ' · '), {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '8px',
        color: '#9ca3af',
      })
      .setOrigin(0.5);
    this.familyBanner.add(zoneLine);

    this.familyBanner.setAlpha(0).setScale(0.6);
    this.tweens.add({
      targets: this.familyBanner,
      alpha: { from: 0, to: 1 },
      scale: { from: 0.6, to: 1 },
      duration: 280,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({
          targets: this.familyBanner,
          alpha: 0,
          delay: 1700,
          duration: 360,
        });
      },
    });
  }

  spawnFloatText(x, y, text, colorInt = 0xffffff) {
    const t = this.add
      .text(x, y, text, {
        fontFamily: '"Press Start 2P", monospace',
        fontSize: '12px',
        color: '#' + colorInt.toString(16).padStart(6, '0'),
        stroke: '#0f0d2c',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(80);
    this.tweens.add({
      targets: t,
      y: y - 28,
      alpha: 0,
      duration: 700,
      ease: 'Quad.easeOut',
      onComplete: () => t.destroy(),
    });
  }

  fireBulletAt(target, isCrit) {
    if (!target || !target.active) return;
    const bullet = new Bullet(this, this.player.x, this.player.y);
    this.playerBullets.add(bullet);
    bullet.targetEnemy = target;
    bullet.fire(target.x, target.y, 0, isCrit); // damage is dealt directly elsewhere; bullet is visual
    this.audio.shoot();
  }

  onAnswer({ idx, option, isCorrect }) {
    if (this.runEnded) return;
    if (!this.activeEnemy || !this.activeEnemy.active) return;

    if (isCorrect) {
      this.handleCorrect(this.activeEnemy);
    } else {
      this.handleWrong(this.activeEnemy);
    }
  }

  handleCorrect(enemy) {
    let damage = CONFIG.PLAYER_DAMAGE_BASE;
    if (this.player.hasDoubleDmg()) damage *= 2;
    let isCrit = false;
    if (this.combo >= CONFIG.COMBO_THRESHOLD_FOR_CRIT && Math.random() < CONFIG.CRIT_CHANCE) {
      isCrit = true;
      damage = Math.floor(damage * CONFIG.CRIT_MULTIPLIER);
    }
    this.combo++;
    if (this.combo > this.runStats.bestCombo) this.runStats.bestCombo = this.combo;
    this.runStats.correct++;
    this.runStats.score += CONFIG.SCORE_PER_CORRECT;

    // Visual bullet (cosmetic). Damage applied directly so bullet can never miss.
    this.fireBulletAt(enemy, isCrit);

    const damageColor = isCrit ? C.CRIT_TEXT : 0xffffff;
    this.spawnFloatText(enemy.x, enemy.y - 24, `-${damage}${isCrit ? '!' : ''}`, damageColor);

    if (isCrit) {
      this.audio.crit();
      // Gold particles
      this.add.particles(enemy.x, enemy.y, 'spark', {
        speed: { min: 100, max: 200 },
        lifespan: 380,
        scale: { start: 1.2, end: 0 },
        quantity: 10,
        tint: 0xfde047,
      }).setDepth(70).explode(10);
    } else {
      this.audio.correct();
    }

    if (enemy.isBoss) {
      this.cameras.main.shake(60, 0.006);
      this.time.timeScale = 0.4;
      this.time.delayedCall(CONFIG.HIT_STOP_BOSS_MS, () => { this.time.timeScale = 1; });
    }

    const dead = enemy.takeDamage(damage);
    if (dead) {
      this.handleEnemyKilled(enemy);
    } else {
      this.events.emit('hud-update');
    }
  }

  handleWrong(enemy) {
    this.combo = 0;
    this.runStats.wrong++;
    this.audio.wrong();
    this.cameras.main.shake(CONFIG.SHAKE_HIT_DURATION, CONFIG.SHAKE_HIT_INTENSITY);
    this.cameras.main.flash(140, 239, 68, 68, true);

    // Apply HP penalty (respects shield, ignores i-frames so it always stings)
    const dealt = this.player.takeDamage(15, { ignoreInvuln: true });
    if (dealt > 0) {
      this.spawnFloatText(this.player.x, this.player.y - 18, '-15', C.DAMAGE_TEXT);
      this.audio.damage();
    }

    if (enemy && enemy.active) {
      enemy.setQuestion(regenerateOptions(enemy.question, this.currentVerbPool, this.askMode));
      this.answerButtons.setQuestion(enemy.question);
    }

    if (this.player.hp <= 0) this.endRun(false);
    else this.events.emit('hud-update');
  }

  handleEnemyKilled(enemy) {
    const wasBoss = enemy.isBoss;
    const x = enemy.x;
    const y = enemy.y;

    // XP / score
    this.runStats.kills++;
    this.runStats.xp += CONFIG.XP_PER_KILL;
    this.runStats.score += wasBoss ? CONFIG.SCORE_PER_BOSS_KILL : CONFIG.SCORE_PER_KILL;

    // Particles
    this.add.particles(x, y, 'particle', {
      speed: { min: 80, max: 180 },
      lifespan: 380,
      scale: { start: 1.1, end: 0 },
      quantity: wasBoss ? 30 : 8,
      tint: wasBoss ? [0xfbbf24, 0xef4444, 0xffffff] : [0xffffff, 0xfbbf24],
    }).setDepth(70).explode(wasBoss ? 30 : 8);

    if (wasBoss) {
      this.cameras.main.shake(CONFIG.SHAKE_BOSS_DURATION, CONFIG.SHAKE_BOSS_INTENSITY);
      this.cameras.main.flash(500, 255, 255, 255, true);
      this.audio.victory();
      this.runStats.bossDefeated = true;
    } else {
      this.spawnSystem.trySpawnPickup(x, y);
      this.audio.hit();
    }

    this.waveSystem.onEnemyKilled(enemy);

    // Clear active reference if this was the active enemy
    if (this.activeEnemy === enemy) this.activeEnemy = null;
    enemy.destroy();

    this.events.emit('hud-update');

    if (wasBoss) {
      this.time.delayedCall(900, () => this.endRun(true));
    }
  }

  onPlayerEnemyContact(player, enemy) {
    if (player.isInvuln() || !player.alive) return;
    const now = this.time.now;
    if (enemy.lastHitAt && now - enemy.lastHitAt < 400) return;
    enemy.lastHitAt = now;

    const dealt = player.takeDamage(enemy.damage);
    if (dealt > 0) {
      this.spawnFloatText(player.x, player.y - 18, `-${dealt}`, C.DAMAGE_TEXT);
      this.audio.damage();
      this.cameras.main.shake(CONFIG.SHAKE_HIT_DURATION, CONFIG.SHAKE_HIT_INTENSITY);
      this.cameras.main.flash(120, 239, 68, 68, true);
      player.applyKnockback(enemy.x, enemy.y);
    }

    if (player.hp <= 0) this.endRun(false);
    else this.events.emit('hud-update');
  }

  onPlayerPickup(player, pickup) {
    const type = pickup.pickupType;
    player.applyPickup(type);
    this.audio.pickup();
    let label = '';
    if (type === 'heart') label = `+${CONFIG.HEART_HP_AMOUNT}HP`;
    else if (type === 'gem') label = `+${CONFIG.GEM_XP_AMOUNT}XP`;
    else if (type === 'speed') label = 'SPEED!';
    else if (type === 'shield') label = 'SHIELD!';
    else if (type === 'doubleDmg') label = 'DMG x2!';
    this.spawnFloatText(pickup.x, pickup.y - 12, label, C.HEAL_TEXT);
    pickup.destroy();
    this.events.emit('hud-update');
  }

  onBulletEnemy(bullet, enemy) {
    // Bullets are cosmetic — damage already applied at fire time. Just consume bullet.
    if (bullet.active) bullet.destroy();
  }

  updateActiveEnemy() {
    if (this.runEnded || !this.player || !this.player.active) return;
    let closest = null;
    let closestDist = Infinity;
    const px = this.player.x;
    const py = this.player.y;
    const list = this.enemies.getChildren();
    for (let i = 0; i < list.length; i++) {
      const e = list[i];
      if (!e.active) continue;
      const dx = e.x - px;
      const dy = e.y - py;
      const d = dx * dx + dy * dy;
      if (d < closestDist) {
        closestDist = d;
        closest = e;
      }
    }
    if (closest !== this.activeEnemy) {
      if (this.activeEnemy && this.activeEnemy.active) {
        this.activeEnemy.setActiveTarget(false);
      }
      this.activeEnemy = closest;
      if (this.activeEnemy) {
        this.activeEnemy.setActiveTarget(true);
        this.answerButtons.setQuestion(this.activeEnemy.question);
      } else {
        this.answerButtons.setQuestion(null);
      }
    }
  }

  update(time, delta) {
    if (this.runEnded) return;

    // Player movement
    let kx = 0;
    let ky = 0;
    const cur = this.cursors;
    const w = this.wasd;
    if (cur.left.isDown || w.A.isDown) kx -= 1;
    if (cur.right.isDown || w.D.isDown) kx += 1;
    if (cur.up.isDown || w.W.isDown) ky -= 1;
    if (cur.down.isDown || w.S.isDown) ky += 1;
    const stick = this.joystick.getVector();
    const useStick = Math.hypot(stick.x, stick.y) > 0.05;
    const vx = useStick ? stick.x : kx;
    const vy = useStick ? stick.y : ky;
    if (this.player.alive) this.player.setMoveVector(vx, vy);

    this.background.tilePositionX += 0.05 * delta * 0.06;
    this.background.tilePositionY += 0.05 * delta * 0.06;

    // Wave tick
    this.waveSystem.update(time, delta);

    // Active enemy tracking
    this.updateActiveEnemy();
  }

  endRun(victory) {
    if (this.runEnded) return;
    this.runEnded = true;

    // Snapshot pre-victory unlock state so VictoryScene can tell first-time runs.
    const saveBefore = SaveSystem.load();
    const wasFirstClear = victory && !saveBefore[`${this.level}Completed`];

    // Mark level completion if boss was defeated
    if (victory) {
      this.runStats[`${this.level}Completed`] = true;
    }
    SaveSystem.recordRun(this.runStats);

    this.scene.stop('HUDScene');
    if (victory) {
      this.scene.start('VictoryScene', {
        runStats: this.runStats,
        level: this.level,
        firstClear: wasFirstClear,
      });
    } else {
      this.audio.gameOver();
      this.scene.start('GameOverScene', { runStats: this.runStats, level: this.level });
    }
  }

  shutdownScene() {
    this.events.off('answer', this.onAnswer, this);
    this.events.off('shield-broken');
    this.events.off('wave-start');
    this.events.off('wave-end');
    this.events.off('wave-boss-start');
    this.events.off('hud-update');
  }
}
